import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import backgroundImage from "../assets/images/risto-landing-final.png";
import mobileBackgroundImage from "../assets/images/mobile-landing-ffp.jpg";
import firstfellowpartnersWhitePng1 from "../assets/images/first-fellow-partners-white.png";
import StaggeredMenu from "./ui/StaggeredMenu";
import { SEOHead } from "./SEOHead";

export const LandingPage = () => {
  const headerRef = useRef(null);
  const headingRef = useRef(null);
  const backgroundRef = useRef(null);
  const mobileBackgroundRef = useRef(null);

  const menuItems = [
    { label: 'Our Manifesto', ariaLabel: 'View our manifesto', link: '/manifesto' },
    { label: 'Family Members', ariaLabel: 'View family members', link: '/family' },
    { label: 'Team', ariaLabel: 'Meet our team', link: '/team' },
    { label: 'References', ariaLabel: 'View references', link: '/references' },
    { label: 'Get in touch', ariaLabel: 'Get in touch with us', link: '/contact' }
  ];

  const socialItems = [
    { label: 'Twitter', link: 'https://twitter.com' },
    { label: 'LinkedIn', link: 'https://linkedin.com' }
  ];

  useEffect(() => {
    // Prevent body scrolling on landing page
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Animate background from black to normal
    const bgRef = backgroundRef.current || mobileBackgroundRef.current;
    if (bgRef) {
      gsap.fromTo(bgRef, 
        { opacity: 0 },
        { opacity: 1, duration: 1.2, ease: "power3.out", delay: 0.2 }
      );
    }

    // Animate header
    gsap.fromTo(headerRef.current, 
      { y: 100, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.1 }
    );

    // Animate heading
    gsap.fromTo(headingRef.current, 
      { y: 150, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.3 }
    );

    // Cleanup: restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  return (
    <main className="bg-black w-full h-[100dvh] flex items-center justify-center relative overflow-hidden">
      <SEOHead
        title="First Fellow Partners | Backing the Entrepreneurs of Tomorrow"
        description="First Fellow Partners is a private investment firm built on a founder-first philosophy. We invest only our own capital, backing bold, mission-driven technology entrepreneurs from idea to lasting impact."
        path="/"
      />
      {/* Desktop background */}
      <img
        ref={backgroundRef}
        className="hidden desktop:block absolute top-0 left-0 w-full h-full object-cover"
        alt="Mountain landscape with hikers"
        src={backgroundImage}
      />
      {/* Mobile background - full width, vertically centered, height may be cropped */}
      <div className="desktop:hidden absolute inset-0 overflow-hidden bg-black">
        <img
          ref={mobileBackgroundRef}
          className="w-full h-full"
          alt="Mountain landscape with hikers"
          src={mobileBackgroundImage}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center center'
          }}
        />
      </div>

      <div ref={headerRef} className="absolute top-[16px] left-0 right-0 flex justify-between items-center px-4 sm:px-6 lg:px-10 z-10">
        <header className="w-[80px] h-[80px] sm:w-[100px] sm:h-[100px] lg:w-[140px] lg:h-[140px] drop-shadow-lg">
          <Link to="/">
            <img
              className="w-full h-full object-contain"
              alt="First Fellow Partners logo"
              src={firstfellowpartnersWhitePng1}
            />
          </Link>
        </header>
        
        {/* StaggeredMenu button on the right */}
        <div className="w-[60px] h-[60px] sm:w-[80px] sm:h-[80px] lg:w-[120px] lg:h-[120px] flex items-center justify-end drop-shadow-lg">
          <StaggeredMenu
            position="right"
            items={menuItems}
            socialItems={socialItems}
            displaySocials={true}
            displayItemNumbering={true}
            menuButtonColor="#fff"
            openMenuButtonColor="#000"
            changeMenuColorOnOpen={true}
            colors={['#1a1a1a', '#424242']}
            logoUrl=""
            accentColor="#000"
            isFixed={true}
            onMenuOpen={() => console.log('Menu opened')}
            onMenuClose={() => console.log('Menu closed')}
          />
        </div>
      </div>

      <section
        ref={headingRef}
        className="absolute bottom-[max(24px,calc(env(safe-area-inset-bottom)+24px))] sm:bottom-[40px] left-[20px] sm:left-[40px] w-[calc(100vw-40px)] sm:w-[600px] lg:w-[800px] z-20"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        aria-labelledby="main-heading"
      >
        <h1
          id="main-heading"
          className="[font-family:'Inter-Bold',Helvetica] font-bold text-white text-[36px] sm:text-[56px] lg:text-[96px] tracking-[0] leading-[32px] sm:leading-[50px] lg:leading-[86px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]"
        >
          <span className="block">BACKING THE</span>
          <span className="block">ENTREPRENEURS</span>
          <span className="block">OF TOMORROW</span>
        </h1>
      </section>
    </main>
  );
};